<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="definition ? definition.title : 'Reports'" :title1="definition ? `${definition.group} report` : ''" />

        <div class="card report-card">
          <div class="card-body">
            <div v-if="catalogError" class="report-alert">{{ catalogError }}</div>

            <template v-else-if="definition">
              <div class="report-header-bar">
                <div>
                  <h5>{{ definition.title }}</h5>
                  <p>{{ definition.description }}</p>
                </div>
                <div v-if="definition.can_export" class="export-actions">
                  <button class="btn btn-outline-success" :disabled="!!exporting || loading" @click="exportReport('excel')">
                    <vue-feather type="download" size="15"></vue-feather>
                    {{ exporting === 'excel' ? 'Preparing...' : 'Excel' }}
                  </button>
                  <button class="btn btn-outline-danger" :disabled="!!exporting || loading" @click="exportReport('pdf')">
                    <vue-feather type="file-text" size="15"></vue-feather>
                    {{ exporting === 'pdf' ? 'Preparing...' : 'PDF' }}
                  </button>
                </div>
              </div>

              <form class="report-toolbar" @submit.prevent="applyFilters">
                <div class="filter-grid">
                  <div v-for="filter in visibleFilters" :key="filter.name" class="form-group">
                    <label :for="`filter-${filter.name}`">{{ filter.label }}</label>
                    <b-form-input
                      v-if="filter.type === 'date'"
                      :id="`filter-${filter.name}`"
                      v-model="filters[filter.name]"
                      type="date"
                      :required="filter.required"
                    />
                    <b-form-select
                      v-else-if="filter.type === 'select'"
                      :id="`filter-${filter.name}`"
                      v-model="filters[filter.name]"
                      :options="selectOptions(filter)"
                    />
                    <b-form-input
                      v-else-if="filter.type === 'number'"
                      :id="`filter-${filter.name}`"
                      v-model="filters[filter.name]"
                      type="number"
                    />
                    <ReportLookupSelect
                      v-else
                      v-model="filters[filter.name]"
                      :type="filter.type"
                      :placeholder="filter.required ? `Choose ${filter.label.toLowerCase()}` : 'All'"
                    />
                  </div>
                  <div class="button-group">
                    <button type="submit" class="btn btn-submit" :disabled="loading">{{ loading ? 'Loading...' : 'View Report' }}</button>
                    <button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="resetFilters">Reset</button>
                  </div>
                </div>
                <ReportDatePresets v-if="hasPeriod" :from="filters.from_date" :to="filters.to_date" @select="applyPeriod" />
              </form>

              <div v-if="reportError" class="report-alert">{{ reportError }}</div>

              <div v-if="loading && !report" class="report-loading">
                <b-spinner small></b-spinner> Loading report...
              </div>

              <div v-if="report" :class="['report-body', { 'is-refreshing': loading }]">
                <div class="report-period">
                  <span class="period-chip"><vue-feather type="calendar" size="13"></vue-feather> {{ report.period }}</span>
                  <span v-for="item in report.filters" :key="item.label" class="filter-chip">{{ item.label }}: <b>{{ item.value }}</b></span>
                  <span class="amount-hint">Amounts in ₹</span>
                </div>

                <div v-if="report.summary.length" class="summary-grid">
                  <div v-for="metric in report.summary" :key="metric.label" :class="['summary-card', `tone-${metric.tone}`]">
                    <span>{{ metric.label }}</span>
                    <strong>{{ formatValue(metric.kind, metric.value, true) }}</strong>
                    <small v-if="metric.hint">{{ metric.hint }}</small>
                  </div>
                </div>

                <section v-for="(section, sectionIndex) in report.sections" :key="sectionIndex" class="report-section">
                  <h6 v-if="section.title">{{ section.title }}</h6>
                  <div class="table-responsive">
                    <table class="report-table">
                      <thead>
                        <tr>
                          <th v-for="column in section.columns" :key="column.key" :class="{ num: isNumeric(column) }">{{ column.label }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!section.rows.length">
                          <td :colspan="section.columns.length" class="empty-cell">{{ section.empty_message }}</td>
                        </tr>
                        <tr v-for="(row, rowIndex) in visibleRows(section, sectionIndex)" :key="rowIndex" :class="[`row-${row._rowtype || 'data'}`, { 'row-zebra': zebraRows[sectionIndex][rowIndex] }]">
                          <td
                            v-for="column in section.columns"
                            :key="column.key"
                            :class="{ num: isNumeric(column), neg: isNegative(column, row[column.key]) }"
                          >{{ formatValue(column.kind, row[column.key]) }}</td>
                        </tr>
                        <tr v-if="section.totals && section.rows.length" class="row-total">
                          <td
                            v-for="column in section.columns"
                            :key="column.key"
                            :class="{ num: isNumeric(column), neg: isNegative(column, section.totals[column.key]) }"
                          >{{ formatValue(column.kind, section.totals[column.key]) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <button
                    v-if="section.rows.length > rowLimit && !expanded[sectionIndex]"
                    type="button"
                    class="btn btn-link show-all"
                    @click="expanded = { ...expanded, [sectionIndex]: true }"
                  >Show all {{ section.rows.length.toLocaleString('en-IN') }} rows</button>
                </section>

                <ul v-if="report.notes.length" class="report-notes">
                  <li v-for="note in report.notes" :key="note">{{ note }}</li>
                </ul>
              </div>
            </template>

            <div v-else-if="!catalogLoading" class="report-alert">
              This report is not available for your account. Ask your administrator for access.
            </div>
            <div v-else class="report-loading"><b-spinner small></b-spinner> Loading...</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import ReportDatePresets from "@/components/report/ReportDatePresets.vue";
import ReportLookupSelect from "@/components/report/ReportLookupSelect.vue";
import { downloadReport, errorMessage, findReportDefinition, getReport } from "@/services/reports";

const ROW_LIMIT = 300;
const NUMERIC_KINDS = new Set(["money", "qty", "number", "percent"]);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatNumber(value, decimals) {
  return Number(value).toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export default {
  name: "ReportViewer",
  components: { ReportDatePresets, ReportLookupSelect },
  data() {
    return {
      definition: null,
      catalogLoading: true,
      catalogError: "",
      filters: {},
      report: null,
      loading: false,
      reportError: "",
      exporting: "",
      expanded: {},
      rowLimit: ROW_LIMIT,
      requestId: 0,
    };
  },
  computed: {
    reportKey() {
      return this.$route.params.key;
    },
    zebraRows() {
      // Per section, whether each row gets the alternate shading (restarts after each group heading).
      if (!this.report) return [];
      return this.report.sections.map((section) => {
        let count = 0;
        return section.rows.map((row) => {
          if ((row._rowtype || "data") !== "data") {
            count = 0;
            return false;
          }
          count += 1;
          return count % 2 === 0;
        });
      });
    },
    hasPeriod() {
      const names = this.visibleFilters.map((filter) => filter.name);
      return names.includes("from_date") && names.includes("to_date");
    },
    visibleFilters() {
      if (!this.definition) return [];
      return this.definition.filters.filter((filter) => !filter.show_when || this.filters[filter.show_when.name] === filter.show_when.value);
    },
  },
  watch: {
    reportKey: {
      immediate: true,
      handler() {
        this.loadDefinition();
      },
    },
  },
  methods: {
    async loadDefinition() {
      const key = this.reportKey;
      this.catalogLoading = true;
      this.catalogError = "";
      this.report = null;
      this.reportError = "";
      try {
        const definition = await findReportDefinition(key);
        if (key !== this.reportKey) return; // navigated away meanwhile
        this.definition = definition;
        if (definition) {
          this.filters = this.initialFilters(definition);
          document.title = `${definition.title} | Reports`;
          this.fetchReport();
        }
      } catch (error) {
        this.catalogError = `Could not load reports: ${await errorMessage(error)}`;
      } finally {
        this.catalogLoading = false;
      }
    },
    initialFilters(definition) {
      const query = this.$route.query;
      return Object.fromEntries(definition.filters.map((filter) => [
        filter.name,
        query[filter.name] !== undefined ? String(query[filter.name]) : filter.default ?? (filter.type === "select" ? "" : null),
      ]));
    },
    requestParams() {
      // Only the filters that are shown count (e.g. the supplier box is ignored for a customer statement).
      return Object.fromEntries(this.visibleFilters.map((filter) => [filter.name, this.filters[filter.name]]));
    },
    missingRequiredLookup() {
      // A required customer / supplier / item has no default; ask for it instead of calling the server.
      return this.visibleFilters.find((filter) => filter.required && !this.filters[filter.name] && !["date", "select", "number"].includes(filter.type));
    },
    async fetchReport() {
      const missing = this.missingRequiredLookup();
      if (missing) {
        this.report = null;
        this.reportError = `Choose a ${missing.label.toLowerCase()} to see this report.`;
        return;
      }
      const requestId = ++this.requestId;
      this.loading = true;
      this.reportError = "";
      try {
        const report = await getReport(this.reportKey, this.requestParams());
        if (requestId !== this.requestId) return;
        this.report = report;
        this.expanded = {};
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.reportError = await errorMessage(error);
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    applyFilters() {
      const query = Object.fromEntries(Object.entries(this.requestParams()).filter(([, value]) => value !== null && value !== ""));
      this.$router.replace({ query }).catch(() => {});
      this.fetchReport();
    },
    applyPeriod({ from, to }) {
      this.filters = { ...this.filters, from_date: from, to_date: to };
      this.applyFilters();
    },
    resetFilters() {
      this.$router.replace({ query: {} }).catch(() => {});
      this.filters = Object.fromEntries(this.definition.filters.map((filter) => [filter.name, filter.default ?? (filter.type === "select" ? "" : null)]));
      this.fetchReport();
    },
    async exportReport(format) {
      const missing = this.missingRequiredLookup();
      if (missing) {
        Swal.fire({ icon: "info", title: `Choose a ${missing.label.toLowerCase()} first` });
        return;
      }
      this.exporting = format;
      try {
        await downloadReport(this.reportKey, this.requestParams(), format);
      } catch (error) {
        Swal.fire({ icon: "error", title: "Export failed", text: await errorMessage(error) });
      } finally {
        this.exporting = "";
      }
    },
    selectOptions(filter) {
      return filter.options.map((option) => ({ value: option.value, text: option.label }));
    },
    visibleRows(section, index) {
      return this.expanded[index] ? section.rows : section.rows.slice(0, ROW_LIMIT);
    },
    isNumeric(column) {
      return NUMERIC_KINDS.has(column.kind);
    },
    isNegative(column, value) {
      return column.kind === "money" && Number(value) < 0;
    },
    formatValue(kind, value, isSummary = false) {
      if (value === null || value === undefined || value === "") return isSummary ? "-" : "";
      switch (kind) {
        case "money":
          return isSummary ? `₹${formatNumber(value, 2)}` : formatNumber(value, 2);
        case "qty":
          return formatNumber(value, Number.isInteger(Number(value)) ? 0 : 2);
        case "number":
          return formatNumber(value, 0);
        case "percent":
          return `${Number(value).toFixed(2)}%`;
        case "date": {
          const [year, month, day] = String(value).slice(0, 10).split("-");
          return day ? `${day} ${MONTHS[Number(month) - 1]} ${year}` : value;
        }
        default:
          return value;
      }
    },
  },
};
</script>

<style scoped src="./report-theme.css"></style>
<style scoped>
.report-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.05);
}
.report-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 14px;
}
.report-header-bar h5 {
  margin: 0 0 4px;
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
}
.report-header-bar p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}
.export-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.export-actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
}
.report-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 14px;
}
.report-loading {
  color: #64748b;
  padding: 24px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.report-body.is-refreshing {
  opacity: 0.55;
  transition: opacity 0.15s;
}
.report-period {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  color: #475569;
  font-size: 13px;
  margin-bottom: 14px;
}
.period-chip,
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
}
.period-chip {
  background: #eff6ff;
  color: #1e3a8a;
  font-weight: 600;
}
.amount-hint {
  margin-left: auto;
  color: #94a3b8;
  font-size: 12px;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}
.summary-card {
  border: 1px solid #e2e8f0;
  border-left: 4px solid #3b82f6;
  border-radius: 10px;
  padding: 12px 14px 12px 16px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.summary-card span {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}
.summary-card strong {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.summary-card small {
  color: #64748b;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.summary-card.tone-positive {
  border-left-color: #10b981;
}
.summary-card.tone-positive strong {
  color: #047857;
}
.summary-card.tone-negative {
  border-left-color: #ef4444;
}
.summary-card.tone-negative strong {
  color: #b91c1c;
}
.report-section {
  margin-bottom: 22px;
}
.report-section h6 {
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}
.table-responsive {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  max-height: 70vh;
  overflow: auto;
}
.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.report-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #1e293b;
  color: #fff;
  font-weight: 600;
  font-size: 12px;
  padding: 10px 12px;
  white-space: nowrap;
  text-align: left;
}
.report-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #eef2f7;
  color: #1e293b;
  vertical-align: top;
}
.report-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.report-table td.neg {
  color: #b91c1c;
}
.report-table tr.row-zebra td {
  background: #f8fafc;
}
.report-table tbody tr.row-data:hover td {
  background: #eff6ff;
}
.report-table tr.row-group td {
  background: #eff6ff;
  color: #1e3a8a;
  font-weight: 700;
}
.report-table tr.row-subtotal td {
  background: #f1f5f9;
  font-weight: 700;
  border-top: 1px solid #cbd5e1;
}
.report-table tr.row-total td {
  background: #dbeafe;
  font-weight: 700;
  border-top: 2px solid #1d4ed8;
  border-bottom: 2px solid #1d4ed8;
}
.empty-cell {
  text-align: center;
  color: #64748b;
  padding: 24px !important;
}
.show-all {
  padding-left: 0;
  font-size: 13px;
}
.report-notes {
  margin: 8px 0 0;
  padding-left: 18px;
  color: #64748b;
  font-size: 12px;
}
@media (max-width: 767px) {
  .report-header-bar {
    flex-direction: column;
  }
  .summary-card strong {
    font-size: 16px;
  }
}
</style>
